import { DILEMMAS } from './games/dilemmaData';
import { PASSWORD_RULES } from './games/passwordRules';
import { CHALLENGES } from './games/robotData';

/**
 * Registry for the /games arcade. Each entry drives both the hub grid and the
 * per-game page header, so a new game only needs one edit here plus its route.
 *
 * `bestKey` is the localStorage key the game writes its personal best to, and
 * `formatBest` turns that raw stored string into hub-card copy. Games with no
 * meaningful "score" (open-ended ones) leave both undefined.
 */
/** Slugs that keep a score. Kept in step with `arcadeGameSlug` in validators. */
export type ArcadeGameSlug =
  | 'circle'
  | 'password'
  | 'forge'
  | 'spend'
  | 'dilemmas'
  | 'clicker'
  | 'deep'
  | 'robot'
  | 'space';

export type FunGame = {
  slug: ArcadeGameSlug;
  title: string;
  emoji: string;
  tagline: string;
  /** Longer blurb used on the game page itself. */
  description: string;
  bestKey?: string;
  formatBest?: (raw: string) => string;
};

/** Namespaced so a stray localStorage key from elsewhere can't collide. */
export const bestKey = (name: string) => `factiii:fun:${name}:best`;

/** Read from the rule list so the copy can't drift when rules are added. */
const PASSWORD_RULE_COUNT = PASSWORD_RULES.length;
const DILEMMA_COUNT = DILEMMAS.length;
const ROBOT_COUNT = CHALLENGES.length;

export const FUN_GAMES: FunGame[] = [
  {
    slug: 'circle',
    title: 'Perfect Circle',
    emoji: '⭕',
    tagline: 'Draw a circle. Find out how bad you are at it.',
    description:
      'Press, sweep all the way around the dot, and let go. We measure how much your radius wobbled.',
    bestKey: bestKey('circle'),
    formatBest: (raw) => `${Number(raw).toFixed(1)}% best`
  },
  {
    slug: 'password',
    title: 'The Password Game',
    emoji: '🔑',
    tagline: 'Choose a password. The rules get worse.',
    description: `Every rule you satisfy unlocks a more unreasonable one. There are ${PASSWORD_RULE_COUNT}, and some of them fight back.`,
    bestKey: bestKey('password'),
    formatBest: (raw) => `${raw}/${PASSWORD_RULE_COUNT} rules cleared`
  },
  {
    slug: 'forge',
    title: 'Fact Forge',
    emoji: '⚗️',
    tagline: 'Fuse ideas into things nobody has made before.',
    description:
      'Start with four elements and click any two to fuse them. Past the hand-built recipes it keeps going — fuse a pair nobody has tried and whatever comes out is credited to you.',
    bestKey: bestKey('forge'),
    formatBest: (raw) => `${raw} discovered`
  },
  {
    slug: 'clicker',
    title: 'Stimulation Clicker',
    emoji: '🧠',
    tagline: 'Click for stimulation. Buy more stimulation.',
    description:
      'Every upgrade adds a little more happening on screen. It does not stop. That is the game.',
    bestKey: bestKey('clicker'),
    formatBest: (raw) => `${Number(raw).toLocaleString()} stimulation`
  },
  {
    slug: 'spend',
    title: 'Spend the Treasury',
    emoji: '💸',
    tagline: 'You have 100 billion trons. Good luck getting rid of them.',
    description:
      'Buy and sell your way to a zero balance. Every purchase lands on the receipt.',
    bestKey: bestKey('spend'),
    formatBest: (raw) => `${Number(raw).toLocaleString()} trons left`
  },
  {
    slug: 'deep',
    title: 'The Deep Sea',
    emoji: '🌊',
    tagline: 'Scroll down. Keep going. It gets darker.',
    description:
      'A dive from the surface to the bottom of the Mariana Trench, with what actually lives at each depth. Every figure is real.',
    bestKey: bestKey('deep'),
    formatBest: (raw) => `${Number(raw).toLocaleString()} m deep`
  },
  {
    slug: 'space',
    title: 'The Size of Space',
    emoji: '🔭',
    tagline: 'From a person to the observable universe, one step at a time.',
    description:
      'Each step multiplies the scale, and the dot shows you what the previous thing has shrunk to.',
    bestKey: bestKey('space'),
    formatBest: (raw) => `reached step ${raw}`
  },
  {
    slug: 'robot',
    title: "I'm Not a Robot",
    emoji: '🤖',
    tagline: `${ROBOT_COUNT} CAPTCHAs. They stop being answerable.`,
    description:
      'Tick the box, then prove it. The interface stays completely deadpan while the questions come apart.',
    bestKey: bestKey('robot'),
    formatBest: (raw) => `${raw}/${ROBOT_COUNT} cleared`
  },
  {
    slug: 'dilemmas',
    title: 'Absurd Dilemmas',
    emoji: '🚋',
    tagline: `${DILEMMA_COUNT} impossible choices, no correct answers.`,
    description: `Pull the lever or do nothing, ${DILEMMA_COUNT} times. Each reveal shows how everyone else actually answered.`,
    bestKey: bestKey('dilemmas'),
    formatBest: (raw) => `pulled the lever ${raw}/${DILEMMA_COUNT}`
  }
];

export const findFunGame = (slug: string) =>
  FUN_GAMES.find((game) => game.slug === slug);
